import { Link, useNavigate } from "react-router-dom";
import moment from "moment/moment";
import {IconButton, Loader, Table, Tooltip, Whisper} from "rsuite";
import React, { useState } from "react";
import DeviceIcons from "../shared/DeviceIcons/DeviceIcon";
import './DevicesTable.scss';
import AdminComponent from "../auth/AdminComponent/AdminComponent";
import StatusControl from "../shared/StatusControl/StatusControl";
import {BsActivity} from "react-icons/bs";
import {DeviceUtils} from "../../shared/Device.utils";
import { IconBaseProps } from "react-icons";
import {CustomCellProps, DevicesTableProps, DeviceTableItem, SortType} from "./DevicesTable.model";
import {BsActivityIcon, RiShieldCheckIcon, RiToolsIcon} from "../../models/Icons.model";

const { Column, HeaderCell, Cell } = Table;

export default function DevicesTable({
  devices,
  loading,
  loadingIcons,
  highlightNew = false
}: DevicesTableProps) {
  const navigate = useNavigate();
  const [sortColumn, setSortColumn] = useState<string | undefined>();
  const [sortType, setSortType] = useState<SortType>();

  const handleSortColumn = (sortColumn?: string, sortType?: SortType) => {
    setSortColumn(sortColumn);
    setSortType(sortType);
  };

  const getSortedData = () => {
    if (sortColumn && sortType) {
      return [...devices].sort((a, b) => {
        const getValue = (obj: DeviceTableItem, key: string) => {
          if (key === 'clockStation.clockSystemSerialNumber') {
            return obj.clockStation?.clockSystemSerialNumber || '';
          } else if (key === 'clockStation.serialNumber') {
            return obj.clockStation?.serialNumber || '';
          }
          return obj[key] || '';
        };

        let valueA: string | number = String(getValue(a, sortColumn));
        let valueB: string | number = String(getValue(b, sortColumn));

        const numA = parseFloat(valueA);
        const numB = parseFloat(valueB);

        if (!isNaN(numA) && !isNaN(numB)) {
          valueA = numA;
          valueB = numB;
        }

        let result = 0;
        if (valueA < valueB) {
          result = -1;
        } else if (valueA > valueB) {
          result = 1;
        }

        return sortType === 'asc' ? result : -result;
      });
    }
    return devices;
  };

  const getUrlImg = (device?: DeviceTableItem | null) => {

    if (!device?.previewImagePath) return undefined;

    if (device.previewImagePath.startsWith('http')) {
      return device.previewImagePath;
    }

    const baseUrl = window.location.origin;
    return `${baseUrl}/${device.previewImagePath}`;
  };

  const DeviceInfoCell = ({ rowData, ...props }: CustomCellProps) => (
    <Cell {...props}>
      <div className='device-wrapper' style={{ display: 'flex', alignItems: 'center' }}>
        <div className="image-container">
          <div style={{
            width: '100px',
            height: '80px',
            borderRadius: '5px',
            overflow: 'hidden',
            marginRight: '8px'
          }}>
            <img
              style={{
                width: '100%',
                height: '100%',
              }}
              src={rowData?.previewImagePath ? getUrlImg(rowData) : "clock.svg"}
              alt="clock-img"
            />
          </div>
        </div>
        <div>
          <p className="device-info">
            {rowData?.clockStation ? rowData?.clockStation?.model : rowData?.model}
            <Whisper placement="top" speaker={
              <Tooltip>
                {moment(rowData?.lastOnlineTime).fromNow()}
              </Tooltip>
            }>
              <IconButton
                className={DeviceUtils.getActivityClassName(rowData?.lastOnlineTime || "")}
                style={{ marginLeft: "10px" }}
                icon={<BsActivityIcon />}
                size="xs"
              />
            </Whisper>
          </p>
          <p className="device-info">
            {rowData?.clockStation
              ? `(${rowData?.clockStation?.firmwareVersion} ${rowData?.controlBlockFirmwareVersion || ''})`
              : rowData?.article
            }
          </p>
          <div className="d-flex device-icons">
            {loadingIcons ? <Loader /> : <DeviceIcons deviceData={rowData as any} className="table-icons-wrapper" />}
          </div>
        </div>
      </div>
    </Cell>
  );

  const DescriptionCell = ({ rowData, ...props }: CustomCellProps) => (
    <Cell {...props}>
      <div className="description-container">
          <span
            className="description description--ellipsis"
          >
            {rowData?.description}
          </span>
      </div>
    </Cell>
  );

  const AdminDescriptionCell = ({ rowData, ...props }: CustomCellProps) => (
    <AdminComponent>
      {() => (
        <Cell {...props}>
          <div className="description-container">
          <span
            className="description description--ellipsis"
           >
            {rowData?.privateDescription}
          </span>
          </div>
        </Cell>
      )}
    </AdminComponent>
  );

  const BigLineDevicesCell = ({ rowData, ...props }: CustomCellProps) => (
    <Cell {...props}>
      <div className="bigline-devices-cell">
        {(rowData?.bigLineDevices ?? []).map((device, index) => (
          <span className="bigline-devices-cell__item" key={`${device.text}-${index}`}>
            <span className={`bigline-devices-cell__status ${device.isConnected ? 'bigline-devices-cell__status--online' : 'bigline-devices-cell__status--offline'}`}/>
            {device.text.split(' | ').pop()}
          </span>
        ))}
      </div>
    </Cell>
  );

  const LastActivityCell = ({ rowData, ...props }: CustomCellProps) => (
    <Cell {...props}>
      <div className="last-activity-cell">
        <StatusControl status={rowData?.isConnected ? "online" : "offline"}/>
        <Link to={`/devices/${rowData?.id}/logs`}>
          {moment(rowData?.lastOnlineTime).fromNow().replace(/\s+назад$/, '')}
        </Link>
      </div>
    </Cell>
  );

  const getRowClassName = (device?: DeviceTableItem) => {
    if (highlightNew && device?.isNew) {
      return 'new-device-row';
    }
    return '';
  };

  const isTodayOrEarlier = (date?: string | null) => {
    if (!date) return false;

    const parsedDate = moment(date, ['YYYY-MM-DD', moment.ISO_8601], true);
    return parsedDate.isValid() && parsedDate.startOf('day').isSameOrBefore(moment().startOf('day'));
  };

  const hasValidWarrantyDate = (date?: string | null) => {
    if (!date) return false;
    return moment(date, ['YYYY-MM-DD', moment.ISO_8601], true).isValid();
  };

  const isWarrantyActive = (date?: string | null) => {
    if (!date) return false;
    const parsedDate = moment(date, ['YYYY-MM-DD', moment.ISO_8601], true);
    return parsedDate.isValid() && parsedDate.startOf('day').isSameOrAfter(moment().startOf('day'));
  };

  const formatAlertDate = (date?: string | null) => {
    if (!date) return '';
    const parsedDate = moment(date, ['YYYY-MM-DD', moment.ISO_8601], true);
    return parsedDate.isValid() ? parsedDate.format('DD.MM.YYYY') : date;
  };

  const getClockChannelSerialNumbers = (device?: DeviceTableItem) => (
    device?.clockStation?.clockChannels
      ?.slice()
      .sort((first, second) => (first.channel ?? Number.MAX_SAFE_INTEGER) - (second.channel ?? Number.MAX_SAFE_INTEGER))
      ?.map(clockChannel => ({
        channel: clockChannel.channel,
        serialNumber: clockChannel.serialNumber?.trim(),
      }))
      .filter((clockChannel): clockChannel is {channel: number | undefined; serialNumber: string} => Boolean(clockChannel.serialNumber)) ?? []
  );

  return (
    <Table
      fillHeight
      data={getSortedData()}
      sortColumn={sortColumn}
      sortType={sortType as any}
      onSortColumn={handleSortColumn}
      loading={loading}
      onRowClick={(rowData: DeviceTableItem) => navigate(`/devices/${rowData.id}`)}
      style={{ verticalAlign: 'middle' }}
      className="custom-table"
      headerHeight={56}
      rowHeight={100}
      rowClassName={getRowClassName}
    >
      <Column width={290} fullText>
        <HeaderCell>Модель часовой станции</HeaderCell>
        <DeviceInfoCell />
      </Column>

      <Column width={80} sortable verticalAlign="middle" align="center">
        <HeaderCell style={{ textAlign: 'center' }}><span className="sortable-header-label">ЭМЧС</span></HeaderCell>
        <Cell style={{ textAlign: 'center' }} dataKey="clockSystemSerialNumber">
          {rowData => (
            <div className="emchs-cell">
              <span className="table-info-title">{rowData?.clockSystemSerialNumber}</span>
              <div className="emchs-cell__alerts">
                {hasValidWarrantyDate(rowData?.warrantyExpirationDate) && (
                  <Whisper
                    placement="top"
                    speaker={<Tooltip>Гарантия действует до {formatAlertDate(rowData.warrantyExpirationDate)}</Tooltip>}
                  >
                    <span
                      className={`emchs-cell__alert emchs-cell__alert--warranty ${
                        isWarrantyActive(rowData.warrantyExpirationDate)
                          ? 'emchs-cell__alert--warranty-active'
                          : 'emchs-cell__alert--warranty-expired'
                      }`}
                      aria-label="Гарантия действует"
                    >
                      <RiShieldCheckIcon />
                    </span>
                  </Whisper>
                )}
                {isTodayOrEarlier(rowData?.nextMaintenanceDate) && (
                  <Whisper
                    placement="top"
                    speaker={<Tooltip>Пройдите ТО. Обратитесь в сервисный центр</Tooltip>}
                  >
                    <span className="emchs-cell__alert" aria-label="Следующее ТО запланировано">
                      <RiToolsIcon />
                    </span>
                  </Whisper>
                )}
              </div>
            </div>
          )}
        </Cell>
      </Column>

      <Column width={135} sortable verticalAlign="middle" align="center">
        <HeaderCell className="long-sort-header" style={{ textAlign: 'center' }}><span className="sortable-header-label">Часовая станция s/n</span></HeaderCell>
        <Cell style={{ textAlign: 'center' }} dataKey="clockStation.serialNumber">
          {rowData => {
            const channelSerialNumbers = getClockChannelSerialNumbers(rowData);

            return (
              <div className="clock-station-serial-cell">
                <span className="table-info-title clock-station-serial-cell__main">
                  {rowData?.clockStation ? rowData.clockStation.serialNumber : rowData?.serialNumber}
                </span>
                {channelSerialNumbers.map((clockChannel, index) => (
                  <span className="clock-station-serial-cell__channel" key={`${clockChannel.channel ?? index}-${clockChannel.serialNumber}`}>
                    {clockChannel.channel != null ? `${clockChannel.channel}к — ` : ''}{clockChannel.serialNumber}
                  </span>
                ))}
              </div>
            );
          }}
        </Cell>
      </Column>

      <Column width={125} sortable verticalAlign="middle" align="center">
        <HeaderCell className="long-sort-header" style={{ textAlign: 'center' }}><span className="sortable-header-label">Версия прошивки</span></HeaderCell>
        <Cell style={{ textAlign: 'center' }} dataKey="firmwareVersion">
          {rowData => (
            <span className="table-info-title">
        {rowData?.firmwareVersion}
      </span>
          )}
        </Cell>
      </Column>

      <Column width={120} >
        <HeaderCell>Название</HeaderCell>
        <DescriptionCell/>
      </Column>

      <Column width={130}>
        <HeaderCell>Описание (админ)</HeaderCell>
        <AdminDescriptionCell />
      </Column>

      <Column width={240} verticalAlign="middle" align="center">
        <HeaderCell>Устройства BIGLine s/n</HeaderCell>
        <BigLineDevicesCell />
      </Column>

      <Column width={135} verticalAlign="middle" align="center">
        <HeaderCell className="last-activity-header">Последняя активность</HeaderCell>
        <LastActivityCell />
      </Column>
    </Table>
  );
}
